import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createElementVNode as _createElementVNode, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "edit-ticket-content__scrollable" }
const _hoisted_2 = {
  key: 0,
  class: "edit-ticket-content__log__heading"
}
const _hoisted_3 = { class: "edit-ticket-content__log__heading" }
const _hoisted_4 = { class: "edit-ticket-content__log__title" }
const _hoisted_5 = {
  key: 0,
  class: "edit-ticket-content__log__body"
}
const _hoisted_6 = { class: "edit-ticket-content__log__body__text" }
const _hoisted_7 = {
  key: 1,
  class: "edit-ticket-content__log__body"
}
const _hoisted_8 = { class: "edit-ticket-content__log__body__text" }
const _hoisted_9 = { class: "edit-ticket-content__log__body__text" }
const _hoisted_10 = {
  key: 2,
  class: "edit-ticket-content__log__body"
}
const _hoisted_11 = {
  key: 1,
  class: "edit-ticket-content__log__body__text"
}
const _hoisted_12 = {
  key: 3,
  class: "edit-ticket-content__log__body edit-ticket-content__log__body__description"
}
const _hoisted_13 = { class: "edit-ticket-content__log__body__description__text" }
const _hoisted_14 = { class: "edit-ticket-content__log__body__description__text" }
const _hoisted_15 = { key: 2 }
const _hoisted_16 = { class: "edit-ticket-content__log__title" }

import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useGeneralStore } from '@/pinia'
import {
  createComment,
  getTicketCategories,
  getTicketCommentsPage,
  getTicketHistoryPage,
  getTicketTypesForCategory,
  Page,
  TicketCategory,
  TicketPatch,
  TicketType
} from '@/services/tickets.service'
import {
  getPriorityColorLight,
  getStatusColorLight,
  translatedPriority,
  translatedStatus
} from '@/utils/ticketHelpers'
import BadgeSquare from '@/design-system/atoms/BadgeSquare.vue'
import DividerHorizontal from '@/design-system/atoms/DividerHorizontal.vue'
import IconArrowNarrowRight from '@/design-system/assets/icons/IconArrowNarrowRight.vue'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import TextAreaButton from '@/design-system/atoms/TextAreaButton.vue'

type TicketLogItem = {
  createdDate: string
  id: string
  type:
    | 'assignee'
    | 'categoryId'
    | 'description'
    | 'dueDate'
    | 'externalId'
    | 'finishedDate'
    | 'log' // From separate "comments" endpoint
    | 'priority'
    | 'startDate'
    | 'status'
    | 'title'
    | 'typeId'
  value: {
    from: string | null | undefined
    to: string | null | undefined
  }
}


export default /*@__PURE__*/_defineComponent({
  __name: 'EditTicketContentLog',
  setup(__props) {

const { locale, t } = useI18n()
const piniaStore = useGeneralStore()
const store = useStore()

const historyItems = ref<TicketLogItem[]>([])
const isLoadingHistory = ref(false)
const isLogValueAdded = ref(false)
const logTextAreaRef = ref()
const ticketCategories = ref<TicketCategory[]>([])
const ticketTypes = ref<TicketType[]>([])

const selectedTicket = computed(() => piniaStore.selectedTicket)

async function createLog(value: string | number) {
  if (!selectedTicket.value) return
  if (value.toString().length) {
    try {
      const comment = await createComment(selectedTicket.value.id, { comment: value.toString() })
      if (comment) {
        logTextAreaRef.value.clearInputValue()
        isLogValueAdded.value = false
        reloadHistory()
      } else {
        store.commit('setInfoMessage', {
          type: 'error',
          message: `${t('general__data__log-create-failed')}`
        })
      }
    } catch (error) {
      store.commit('setInfoMessage', {
        type: 'error',
        message: `${t('general__data__log-create-failed')}`
      })
    }
  }
}
function handleLogInput(value: string | number) {
  if (value.toString().length) isLogValueAdded.value = true
  else isLogValueAdded.value = false
}
function formatTitleDate(date: string) {
  let dateString = new Date(date).toLocaleTimeString(locale.value, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
  if (locale.value === 'sv') {
    dateString = dateString.replace(' ', ' kl. ')
  }
  return dateString
}
function generateHistoryItems(historyContent: TicketPatch[]): TicketLogItem[] {
  const historyArray: TicketLogItem[] = []
  historyContent.forEach((historyItem) => {
    const createdDate = historyItem.createdDate ?? ''
    if (historyItem.assignee) {
      historyArray.push({
        createdDate: createdDate,
        id: historyItem.id + '_assignee',
        type: 'assignee',
        value: { from: historyItem.assignee.from, to: historyItem.assignee.to }
      })
    }
    if (historyItem.categoryId) {
      historyArray.push({
        createdDate: createdDate,
        id: historyItem.id + '_categoryId',
        type: 'categoryId',
        value: { from: historyItem.categoryId.from, to: historyItem.categoryId.to }
      })
    }
    if (historyItem.description) {
      historyArray.push({
        createdDate: createdDate,
        id: historyItem.id + '_description',
        type: 'description',
        value: { from: historyItem.description.from, to: historyItem.description.to }
      })
    }
    if (historyItem.dueDate) {
      historyArray.push({
        createdDate: createdDate,
        id: historyItem.id + '_dueDate',
        type: 'dueDate',
        value: { from: historyItem.dueDate.from, to: historyItem.dueDate.to }
      })
    }
    if (historyItem.externalId) {
      historyArray.push({
        createdDate: createdDate,
        id: historyItem.id + '_externalId',
        type: 'externalId',
        value: { from: historyItem.externalId.from, to: historyItem.externalId.to }
      })
    }
    if (historyItem.finishedDate) {
      historyArray.push({
        createdDate: createdDate,
        id: historyItem.id + '_finishedDate',
        type: 'finishedDate',
        value: { from: historyItem.finishedDate.from, to: historyItem.finishedDate.to }
      })
    }
    if (historyItem.priority) {
      historyArray.push({
        createdDate: createdDate,
        id: historyItem.id + '_priority',
        type: 'priority',
        value: { from: historyItem.priority.from, to: historyItem.priority.to }
      })
    }
    if (historyItem.startDate) {
      historyArray.push({
        createdDate: createdDate,
        id: historyItem.id + '_startDate',
        type: 'startDate',
        value: { from: historyItem.startDate.from, to: historyItem.startDate.to }
      })
    }
    if (historyItem.status) {
      historyArray.push({
        createdDate: createdDate,
        id: historyItem.id + '_status',
        type: 'status',
        value: { from: historyItem.status.from, to: historyItem.status.to }
      })
    }
    if (historyItem.title) {
      historyArray.push({
        createdDate: createdDate,
        id: historyItem.id + '_title',
        type: 'title',
        value: { from: historyItem.title.from, to: historyItem.title.to }
      })
    }
    if (historyItem.typeId) {
      historyArray.push({
        createdDate: createdDate,
        id: historyItem.id + '_typeId',
        type: 'typeId',
        value: { from: historyItem.typeId.from, to: historyItem.typeId.to }
      })
    }
  })
  return historyArray
}
function getCustomColor(historyType: string, value: string | null | undefined) {
  switch (historyType) {
    case 'priority':
      return value ? getPriorityColorLight(value) : undefined
    case 'status':
      return value ? getStatusColorLight(value) : undefined
    default:
      return undefined
  }
}
function getFormattedValue(historyType: string, value: string | null | undefined) {
  let formattedValue = value
  if (historyType === 'dueDate' || historyType === 'finishedDate' || historyType === 'startDate')
    formattedValue = formattedValue?.length
      ? new Date(formattedValue).toLocaleDateString()
      : t('general__text__none-variant')
  if (historyType === 'priority')
    formattedValue = formattedValue?.length
      ? translatedPriority(formattedValue)
      : t('general__text__unknown')
  if (historyType === 'status')
    formattedValue = formattedValue?.length
      ? translatedStatus(formattedValue)
      : t('general__text__unknown')
  if (historyType === 'categoryId') {
    if (!formattedValue?.length) return t('general__text__none')
    const categoryName = ticketCategories.value.find((category) => category.id === formattedValue)
      ?.name[locale.value]
    formattedValue = categoryName ?? t('general__text__unknown')
  }
  if (historyType === 'typeId') {
    if (!formattedValue?.length) return t('general__text__none')
    const typeName = ticketTypes.value.find((type) => type.id === formattedValue)?.name[
      locale.value
    ]
    formattedValue = typeName ?? t('general__text__unknown')
  }
  if (historyType === 'assignee' || historyType === 'description' || historyType === 'title') {
    if (!formattedValue?.length) return t('general__text__none')
  }
  if (historyType === 'externalId') {
    if (!formattedValue?.length) return t('general__text__none-variant')
  }
  return formattedValue ?? ''
}
function getTitle(historyType: string) {
  switch (historyType) {
    case 'assignee':
      return t('general__text__assignee') + ' ' + t('general__text__changed').toLowerCase()
    case 'categoryId':
      return t('general__text__category') + ' ' + t('general__text__changed').toLowerCase()
    case 'description':
      return t('general__text__description') + ' ' + t('general__text__updated-was').toLowerCase()
    case 'dueDate':
      return t('general__text__due-date') + ' ' + t('general__text__updated-was').toLowerCase()
    case 'externalId':
      return t('general__text__external-id') + ' ' + t('general__text__changed').toLowerCase()
    case 'finishedDate':
      return t('general__text__finished-date') + ' ' + t('general__text__updated-was').toLowerCase()
    case 'log':
      return t('general__text__log')
    case 'priority':
      return t('general__text__priority') + ' ' + t('general__text__changed').toLowerCase()
    case 'startDate':
      return t('general__text__start-date') + ' ' + t('general__text__updated-was').toLowerCase()
    case 'status':
      return t('general__text__status') + ' ' + t('general__text__changed').toLowerCase()
    case 'title':
      return t('general__data__ticket-name') + ' ' + t('general__text__changed').toLowerCase()
    case 'typeId':
      return t('general__text__type') + ' ' + t('general__text__changed').toLowerCase()
    default:
      return ''
  }
}
async function reloadHistory() {
  historyItems.value = []
  ticketCategories.value = []
  ticketTypes.value = []
  isLoadingHistory.value = true
  if (!selectedTicket.value) return
  const historyResponse: Page<TicketPatch> | undefined = await getTicketHistoryPage(
    selectedTicket.value.id,
    0,
    500
  )
  if (!historyResponse) return
  if (historyResponse.content.some((historyItem) => historyItem.categoryId || historyItem.typeId)) {
    // Categories request is necessary for both categoryId and typeId
    const categoryResponse = await getTicketCategories()
    if (categoryResponse?.length) ticketCategories.value = categoryResponse
    // The types request is only necessary if there is any typeId in the history
    if (
      ticketCategories.value.length &&
      historyResponse.content.some((historyItem) => historyItem.typeId)
    ) {
      const typesArray: TicketType[] = []
      ticketCategories.value.forEach(async (category) => {
        const typesResponse = await getTicketTypesForCategory(category.id)
        if (typesResponse?.length) typesArray.push(...typesResponse)
      })
      ticketTypes.value = typesArray
    }
  }
  const historyArray: TicketLogItem[] = generateHistoryItems(historyResponse.content)
  const commentsResponse = await getTicketCommentsPage(selectedTicket.value.id, 0, 500)
  if (commentsResponse?.content.length) {
    commentsResponse.content.forEach((comment) => {
      historyArray.push({
        createdDate: comment.createdDate,
        id: comment.id,
        type: 'log',
        value: { from: null, to: comment.comment }
      })
    })
  }
  historyArray.sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())
  historyItems.value = historyArray
  isLoadingHistory.value = false
}

onMounted(() => {
  reloadHistory()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    (_unref(store).getters.hasErrandEditorAccess && selectedTicket.value?.active)
      ? (_openBlock(), _createElementBlock("h5", _hoisted_2, _toDisplayString(_unref(t)('general__text__log')), 1))
      : _createCommentVNode("", true),
    (_unref(store).getters.hasErrandEditorAccess && selectedTicket.value?.active)
      ? (_openBlock(), _createBlock(TextAreaButton, {
          key: 1,
          ref_key: "logTextAreaRef",
          ref: logTextAreaRef,
          buttonText: _unref(t)('general__text__update') + ' ' + _unref(t)('general__data__ticket-the').toLowerCase(),
          placeHolderText: _unref(t)('general__data__ticket-add-to-log'),
          disabled: !isLogValueAdded.value,
          style: {"margin-bottom":"32px"},
          onEmitInput: handleLogInput,
          onOnClicked: createLog
        }, null, 8, ["buttonText", "placeHolderText", "disabled"]))
      : _createCommentVNode("", true),
    _createElementVNode("h5", _hoisted_3, _toDisplayString(_unref(t)('general__text__history')), 1),
    _createVNode(PulseLoader, {
      loading: isLoadingHistory.value,
      class: "spinner",
      color: "#1d234f"
    }, null, 8, ["loading"]),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(historyItems.value, (item) => {
      return (_openBlock(), _createElementBlock("article", {
        key: item.id
      }, [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("p", null, _toDisplayString(getTitle(item.type)), 1),
          _createElementVNode("p", null, _toDisplayString(formatTitleDate(item.createdDate)), 1)
        ]),
        (item.type === 'log')
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createElementVNode("p", _hoisted_6, _toDisplayString(item.value.to), 1)
            ]))
          : _createCommentVNode("", true),
        (
          item.type === 'assignee' ||
          item.type === 'dueDate' ||
          item.type === 'externalId' ||
          item.type === 'finishedDate' ||
          item.type === 'startDate' ||
          item.type === 'title'
        )
          ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
              _createElementVNode("p", _hoisted_8, _toDisplayString(getFormattedValue(item.type, item.value.from)), 1),
              _createVNode(IconArrowNarrowRight, { class: "edit-ticket-content__log__body__arrow" }),
              _createElementVNode("p", _hoisted_9, _toDisplayString(getFormattedValue(item.type, item.value.to)), 1)
            ]))
          : _createCommentVNode("", true),
        (
          item.type === 'categoryId' ||
          item.type === 'priority' ||
          item.type === 'status' ||
          item.type === 'typeId'
        )
          ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
              (item.value.from?.length)
                ? (_openBlock(), _createBlock(BadgeSquare, {
                    key: item.id,
                    badgeText: getFormattedValue(item.type, item.value.from),
                    customColor: getCustomColor(item.type, item.value.from),
                    size: "small"
                  }, null, 8, ["badgeText", "customColor"]))
                : (_openBlock(), _createElementBlock("p", _hoisted_11, _toDisplayString(getFormattedValue(item.type, item.value.from)), 1)),
              _createVNode(IconArrowNarrowRight, { class: "edit-ticket-content__log__body__arrow" }),
              (_openBlock(), _createBlock(BadgeSquare, {
                key: item.id,
                badgeText: getFormattedValue(item.type, item.value.to),
                customColor: getCustomColor(item.type, item.value.to),
                size: "small"
              }, null, 8, ["badgeText", "customColor"]))
            ]))
          : _createCommentVNode("", true),
        (item.type === 'description')
          ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
              _createElementVNode("p", _hoisted_13, _toDisplayString(getFormattedValue(item.type, item.value.from)), 1),
              _createVNode(IconArrowNarrowRight, { class: "edit-ticket-content__log__body__arrow" }),
              _createElementVNode("p", _hoisted_14, _toDisplayString(getFormattedValue(item.type, item.value.to)), 1)
            ]))
          : _createCommentVNode("", true),
        _createVNode(DividerHorizontal, { style: {"margin":"16px 0"} })
      ]))
    }), 128)),
    (!isLoadingHistory.value)
      ? (_openBlock(), _createElementBlock("article", _hoisted_15, [
          _createElementVNode("div", _hoisted_16, [
            _createElementVNode("p", null, _toDisplayString(_unref(t)('general__data__ticket-the')) + " " + _toDisplayString(_unref(t)('general__text__created-was').toLowerCase()), 1),
            _createElementVNode("p", null, _toDisplayString(selectedTicket.value?.createdDate ? formatTitleDate(selectedTicket.value.createdDate) : ''), 1)
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})